import { createPageUrl } from '@/components/utils';

export function isNavigationItemVisible(item, ctx) {
  if (typeof item.showIf === 'function') return !!item.showIf(ctx);
  if (item.adminOnly && !ctx.hasPermission('roles.manage')) return false;
  return !item.permission || ctx.hasPermission(item.permission);
}

export function navigationHref(item, ctx = {}) {
  return item.pathFor ? item.pathFor(ctx) : (item.path || createPageUrl(item.pageName, item.params));
}

// Keep legacy pageName pins for canonical destinations; tab shortcuts need an
// identity of their own so pinning Stock never silently pins the product list.
export function navigationId(item) {
  return item.id || item.pageName;
}

export function visibleNavigation(navigation, ctx) {
  return navigation.flatMap(item => {
    if (!isNavigationItemVisible(item, ctx)) return [];
    if (!item.subItems) return [item];
    const subItems = item.subItems.filter(sub => sub.type !== 'label' && isNavigationItemVisible(sub, ctx));
    return subItems.length ? [{ ...item, subItems }] : [];
  });
}

export function activeNavigationItem(items, location, ctx = {}, currentPageName) {
  const query = new URLSearchParams(location.search);
  const path = location.pathname.toLowerCase().replace(/\/$/, '') || '/';
  const candidates = items.flatMap(item => item.subItems || [item]);
  const matches = candidates.map(item => {
    const [rawPath, rawQuery = ''] = navigationHref(item, ctx).split('?');
    const target = rawPath.toLowerCase().replace(/\/$/, '') || '/';
    const params = [...new URLSearchParams(rawQuery)];
    const pathMatches = path === target || (target !== '/' && path.startsWith(target + '/'));
    if (!pathMatches || !params.every(([key, value]) => query.get(key) === value)) return null;
    return { item, score: target.length + params.length * 1000 };
  }).filter(Boolean).sort((a, b) => b.score - a.score);
  return matches[0]?.item || candidates.find(item => item.pageName === currentPageName && !item.id);
}

// Search exposes the same destinations plus useful direct links into existing
// tabs. These remain feature-owned metadata, not a second navigation tree.
export function navigationSearchItems(items) {
  return items.flatMap(item => [item, ...(item.shortcuts || []).map(shortcut => ({
    ...item, ...shortcut, shortcuts: undefined,
    id: `${navigationId(item)}:${shortcut.params?.tab || shortcut.nameKey}`,
    path: shortcut.path || createPageUrl(item.pageName, shortcut.params),
  }))]);
}
